import { useMemo, useState } from 'react';
import { BarChart3, Trophy, AlertTriangle } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SegmentChips } from '@/components/common/SegmentChips';
import { segParam } from '@/lib/segments';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

// Sequence-suffix stripper (mirrors backend campaign_codes._strip_seq) so that
// mission hours (stored at base-code level) merge with their sub-codes → correct
// productivity (BS validés / heure) per mission in live mode.
const SEQ_RE = /^\d{2}[A-Z]{3,}\d{2}[A-Z]$/;
const baseCode = (code) => (SEQ_RE.test(code || '') ? String(code).slice(0, -1) : code);

export default function Performances() {
  const { body, clearAll, parrainageOn, parrainageSeuil } = useFilters();
  const [seg, setSeg] = useState('ensemble');
  const segBody = parrainageOn ? { ...body, segment: segParam(seg), parrainage_seuil: parrainageSeuil } : body;
  const { data, isLoading } = usePost('/analytics/breakdown', { ...segBody, dim: 'campaign_code' });
  const raw = data?.items || [];

  // Aggregate per mission (base code): sums hours + BS across sub-codes so
  // transformation & productivity are computed at the level hours are tracked.
  const items = useMemo(() => {
    const map = new Map();
    for (const it of raw) {
      const key = baseCode(it.key);
      const g = map.get(key) || { key, bs_rue: 0, bs_reel: 0, heures: 0, montant: 0,
        canal: it.canal, region: it.region, label: it.label };
      g.bs_rue += it.bs_rue || 0;
      g.bs_reel += it.bs_reel || 0;
      g.heures += it.heures || 0;
      g.montant += it.montant || 0;
      map.set(key, g);
    }
    return [...map.values()]
      .map((g) => ({
        ...g,
        taux_transfo: g.bs_rue ? g.bs_reel / g.bs_rue : 0,
        productivite: g.heures ? g.bs_reel / g.heures : 0,
        don_moyen: g.bs_reel ? g.montant / g.bs_reel : 0,
      }))
      .sort((a, b) => b.bs_reel - a.bs_reel);
  }, [raw]);

  // Only rank missions with a statistically meaningful sample: a rate on 5 BS
  // is not comparable to one on 500. Pick the highest floor that still yields
  // at least 3 missions (50 → 20 → 0).
  const minSample = useMemo(() => {
    for (const t of [50, 20, 0]) {
      if (items.filter((i) => i.bs_rue > 0 && i.bs_reel >= t).length >= 3) return t;
    }
    return 0;
  }, [items]);
  const relevant = useMemo(
    () => items.filter((i) => i.bs_rue > 0 && i.bs_reel >= minSample),
    [items, minSample]
  );
  const best = [...relevant].sort((a, b) => b.taux_transfo - a.taux_transfo).slice(0, 3);
  const attention = [...relevant].sort((a, b) => a.taux_transfo - b.taux_transfo).slice(0, 3);
  // Only missions with recorded hours are meaningful for the productivity chart.
  const prodItems = items.filter((i) => i.heures > 0);
  const geo = (c) => [c.canal, c.region].filter(Boolean).join(' · ');
  // Small canal · région subtitle under each campaign code on the axis charts.
  const subLabels = useMemo(() => {
    const m = {};
    for (const c of items) { const g = geo(c); if (g) m[c.key] = g; }
    return m;
  }, [items]);

  return (
    <div className="space-y-6">
      <PageHeader title="Performances" icon={BarChart3}
        subtitle="Comparez et classez vos codes campagne" />
      {parrainageOn && (
        <SegmentChips single value={seg} onChange={setSeg} testid="performances-segment" />
      )}

      {isLoading ? <CardSkeleton /> : items.length === 0 ? <EmptyState onClear={clearAll} /> : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <SectionCard title="Meilleures performances" subtitle={`Top taux de transformation · min. ${minSample} BS validés`}
              action={<Trophy className="h-5 w-5 text-amber-500" />}>
              <div className="space-y-2">
                {best.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Pas assez de volume pour un classement pertinent.</p>}
                {best.map((c, i) => (
                  <div key={c.key} className="flex items-center gap-3 rounded-xl border bg-card p-3" data-testid="perf-best-row">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full accent-bg-100 font-display text-sm font-bold accent-text">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-sm font-semibold">{c.key}</span>
                      {geo(c) && <span className="block break-words text-[11px] text-muted-foreground">{geo(c)}</span>}
                    </div>
                    <Badge variant="secondary" className="shrink-0 rounded-full">{fmtPct(c.taux_transfo)}</Badge>
                    <span className="shrink-0 text-xs text-muted-foreground tabular">{fmtInt(c.bs_reel)} BS</span>
                  </div>
                ))}
              </div>
            </SectionCard>
            <SectionCard title="Points de vigilance" subtitle={`Taux de transformation les plus faibles · min. ${minSample} BS validés`}
              action={<AlertTriangle className="h-5 w-5 text-amber-500" />}>
              <div className="space-y-2">
                {attention.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Pas assez de volume pour un classement pertinent.</p>}
                {attention.map((c) => (
                  <div key={c.key} className="flex items-center gap-3 rounded-xl border bg-card p-3" data-testid="perf-worst-row">
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-sm font-semibold">{c.key}</span>
                      {geo(c) && <span className="block break-words text-[11px] text-muted-foreground">{geo(c)}</span>}
                    </div>
                    <Badge variant="outline" className="shrink-0 rounded-full border-amber-300 text-amber-700">{fmtPct(c.taux_transfo)}</Badge>
                    <span className="shrink-0 text-xs text-muted-foreground tabular">{fmtInt(c.bs_reel)} BS</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          <SectionCard title="Comparaison des codes campagne" subtitle="BS signés vs BS validés (par mission)">
            <ComparisonBars data={items.filter((c) => c.bs_rue > 0 || c.bs_reel > 0).map((c) => ({ period: c.key, bs_rue: c.bs_rue, bs_reel: c.bs_reel }))}
              subLabels={subLabels} height={360} xTickFormatter={(v) => v} formatTip={(v) => fmtInt(v)}
              series={[{ key: 'bs_rue', name: 'BS signés', color: '#C7CDD6' }, { key: 'bs_reel', name: 'BS validés', color: 'var(--tenant-accent-500)' }]} />
          </SectionCard>

          <SectionCard title="Productivité horaire par code" subtitle="BS validés / heure de rue (par mission)">
            {prodItems.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="perf-prod-empty">
                Aucune heure disponible sur la période. Importez les heures RH (Admin → Heures RH) pour activer la productivité.
              </p>
            ) : (
              <ComparisonBars data={prodItems.map((c) => ({ period: c.key, productivite: Number(c.productivite.toFixed(2)) }))}
                subLabels={subLabels} height={340} xTickFormatter={(v) => v} formatTip={(v) => `${fmtNum(v, 2)} BS/h`}
                series={[{ key: 'productivite', name: 'Productivité', color: 'var(--tenant-accent-500)' }]} />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
